import { NgClass } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
} from '@angular/core'

import { FlxStyleModule, FlxThemeModule, FlxWrapperStyleDirective } from '@flowx/angular-theme'
import { StepIndicatorStep, StepIndicatorTemplateConfig } from '@flowx/core-sdk'

import { getBoolExpressionSignal, getComputedValueSignal } from '../helpers'

type StepState = 'completed' | 'active' | 'upcoming'
type StepIndicatorOrientation = 'vertical' | 'horizontal'

@Component({
  selector: 'flx-step-indicator-wrapper',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
  ],
  imports: [NgClass, FlxThemeModule, FlxStyleModule],
  templateUrl: './step-indicator-wrapper.component.html',
  styleUrl: './step-indicator-wrapper.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlxStepIndicatorWrapperComponent implements OnInit, AfterViewInit {
  readonly #injector = inject(Injector)
  readonly #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  templateConfig = input.required<StepIndicatorTemplateConfig>()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  style = computed(() => this.displayOptions().style)

  readonly isDynamicDataSource = computed(() => !!this.flowxProps()?.isDynamicDataSource)

  // expressions
  isHidden: Signal<boolean>
  // dynamic props
  stepsSource: Signal<StepIndicatorStep[]>

  readonly steps = computed(() => {
    if (this.isDynamicDataSource()) {
      const resolved = this.stepsSource()
      return Array.isArray(resolved) ? resolved : []
    }
    return this.flowxProps()?.steps ?? []
  })
  readonly activeIndex = computed(() => this.flowxProps()?.activeIndex)
  readonly orientation = computed(
    () => (this.style() as { orientation?: StepIndicatorOrientation })?.orientation ?? 'horizontal'
  )

  readonly hostClass = computed(() =>
    [
      this.displayOptions().className,
      this.orientation() === 'vertical' ? 'step-indicator--vertical' : null,
    ]
      .filter(Boolean)
      .join(' ')
  )

  readonly stepState = (index: number): StepState => {
    const activeIndex = this.activeIndex()

    if (activeIndex === null || activeIndex === undefined) {
      return 'upcoming'
    }

    if (index < activeIndex) {
      return 'completed'
    }

    return index === activeIndex ? 'active' : 'upcoming'
  }

  readonly circleClass = (state: StepState): string => `step-indicator__circle--${state}`

  ngOnInit(): void {
    runInInjectionContext(this.#injector, () => {
      const props = this.flowxProps()
      const templateConfig = this.templateConfig()
      this.isHidden = getBoolExpressionSignal(props.hide, templateConfig)
      // `getComputedValueSignal` evaluates the expression via `new Function` and preserves the
      // raw resolved value (array), unlike `getDynamicPropSignal` which stringifies via text
      // interpolation — required to keep `steps` as actual StepIndicatorStep objects.
      this.stepsSource = getComputedValueSignal(
        props.stepsSource || '[]',
        templateConfig
      ) as unknown as Signal<StepIndicatorStep[]>
    })
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }
}
